// src/pages/DashboardPage.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import api from '../utils/api';

const COLORS = ['#d4940a', '#1a1a2e', '#52c41a', '#1890ff', '#722ed1', '#eb2f96', '#fa8c16'];

const StatCard = ({ label, value, color, icon }) => (
  <div style={{
    background: '#fff', borderRadius: 10, padding: '20px 24px',
    borderLeft: `4px solid ${color}`, boxShadow: '0 2px 8px rgba(0,0,0,.08)',
    display: 'flex', alignItems: 'center', gap: 16, flex: 1, minWidth: 160,
  }}>
    <span style={{ fontSize: 32 }}>{icon}</span>
    <div>
      <div style={{ fontSize: 28, fontWeight: 700, color }}>{value ?? '–'}</div>
      <div style={{ fontSize: 13, color: '#666' }}>{label}</div>
    </div>
  </div>
);

export default function DashboardPage() {
  //const today = new Date().toISOString().split('T')[0];
    const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
    const today = new Date();

    // Creamos una copia de "today" y le restamos 8 días
    const eightDaysAgo = new Date();
    eightDaysAgo.setDate(today.getDate() - 8);

    const [fechaInicio, setFechaInicio] = useState(
        eightDaysAgo.toISOString().split('T')[0]  // ← string "YYYY-MM-DD"
    );
    const [fechaFin, setFechaFin] = useState(
        today.toISOString().split('T')[0]          // ← string "YYYY-MM-DD"
    );
    //const [fechaInicio, setFechaInicio] = useState(eightDaysAgo);
    ////const [fechaInicio, setFechaInicio] = useState(monthAgo);
    //const [fechaFin, setFechaFin] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/dashboard?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`);
      setData(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [fechaInicio, fechaFin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const s = {
    title: { fontSize: 22, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 },
    sub: { color: '#888', fontSize: 13, marginBottom: 20 },
    row: { display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 },
    card: { background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,.08)', flex: 1, minWidth: 300 },
    filters: { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20, background: '#fff', padding: '14px 20px', borderRadius: 10, flexWrap: 'wrap' },
    input: { padding: '7px 12px', border: '1px solid #d9d9d9', borderRadius: 6, fontSize: 13 },
    btn: { padding: '8px 20px', background: '#d4940a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 },
  };

  // Formato sexo legible
  const porSexoFormatted = (data?.por_sexo || []).map(d => ({ ...d, nombre: d.sexo === 'M' ? 'Masculino' : 'Femenino' }));
  const porDiaFormatted = (data?.por_dia || []).map(d => ({ ...d, fecha: d.fecha?.split('T')[0] }));

  return (
    <div>
      <div style={s.title}>📊 Dashboard Gerencial</div>
      <div style={s.sub}>Vista general de movilización de personal</div>

      {/* Filtros */}
      <div style={s.filters}>
        <label style={{ fontSize: 13, fontWeight: 600 }}>Período:</label>
        <input type="date" style={s.input} value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} />
        <span>–</span>
        <input type="date" style={s.input} value={fechaFin} onChange={e => setFechaFin(e.target.value)} />
        <button style={s.btn} onClick={fetchData}>{loading ? 'Cargando...' : '🔍 Aplicar'}</button>
      </div>

      {/* KPIs */}
      <div style={s.row}>
        <StatCard label="Total Subidas" value={data?.totales?.total_subida} color="#52c41a" icon="🔼" />
        <StatCard label="Total Bajadas" value={data?.totales?.total_bajada} color="#1890ff" icon="🔽" />
        <StatCard label="Total Movilizados" value={data?.totales?.total_general} color="#d4940a" icon="👷" />
        <StatCard label="Viajes En Ruta" value={data?.viajes_activos?.length} color="#722ed1" icon="🚌" />
      </div>

      {/* Gráficos fila 1 */}
      <div style={s.row}>
        <div style={s.card}>
          <h3 style={{ marginTop: 0, fontSize: 15 }}>👷 Por Origen (Provincia)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data?.por_origen || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="origen" tick={{ fontSize: 12 }} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="total" fill="#d4940a" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={s.card}>
          <h3 style={{ marginTop: 0, fontSize: 15 }}>⚧ Por Sexo</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={porSexoFormatted} dataKey="total" nameKey="nombre" cx="50%" cy="50%" outerRadius={80} label={({ nombre, total }) => `${nombre}: ${total}`}>
                {porSexoFormatted.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Gráfico línea temporal */}
      <div style={s.row}>
        <div style={{ ...s.card, flex: 'none', width: '100%' }}>
          <h3 style={{ marginTop: 0, fontSize: 15 }}>📅 Movimiento por Día</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={porDiaFormatted}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="subida" stroke="#52c41a" strokeWidth={2} dot={false} name="Subida" />
              <Line type="monotone" dataKey="bajada" stroke="#1890ff" strokeWidth={2} dot={false} name="Bajada" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top cargos */}
      <div style={s.row}>
        <div style={{ ...s.card, flex: 'none', width: '100%' }}>
          <h3 style={{ marginTop: 0, fontSize: 15 }}>🎖 Por Cargo (Top 10)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={(data?.por_cargo || []).slice(0, 10)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="cargo" type="category" width={140} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="total" fill="#1890ff" radius={[0,4,4,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
